// The filled instrument symbols of the Marina look (docs/design/marina-system
// DESIGN_SYSTEM.md §4, cloud ADR 0396, TOM-679): a solid bolt for power, a
// layered droplet for water, a pump with its hose for fuel, a filled sun, cloud
// and crescent moon for the weather. The shapes are the design package's own
// (home.html's icon library); the rest follow its 24-unit grid. Every colour is
// a token: the symbol's own colour is `currentColor`, set by the card from its
// kind (`--chart-kind-icon`), and the sun, moon and cloud take their own roles.
// Decorative: the card's title names what it shows.

import { useId, type ReactNode } from 'react';

export type GlyphName =
  | 'anchor'
  | 'barometer'
  | 'bolt'
  | 'camera'
  | 'cloud'
  | 'depth'
  | 'engine'
  | 'fuel'
  | 'helm'
  | 'moon'
  | 'partly-cloudy'
  | 'pin'
  | 'spark'
  | 'speaker'
  | 'sun'
  | 'thermometer'
  | 'tide'
  | 'vessel'
  | 'warning'
  | 'waste'
  | 'water'
  | 'wind';

function SunDisc({ id, cx, cy, r }: { id: string; cx: number; cy: number; r: number }) {
  return (
    <>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" style={{ stopColor: 'var(--tm-sun-highlight)' }} />
          <stop offset="1" style={{ stopColor: 'var(--tm-sun)' }} />
        </linearGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill={`url(#${id})`} />
    </>
  );
}

function CloudFill({ id }: { id: string }) {
  return (
    <defs>
      <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" style={{ stopColor: 'var(--tm-cloud-highlight)' }} />
        <stop offset="1" style={{ stopColor: 'var(--tm-cloud-shadow)' }} />
      </linearGradient>
    </defs>
  );
}

function body(name: GlyphName, id: string): ReactNode {
  switch (name) {
    case 'bolt':
      return <path d="m13 2-9 12h7l-1 8 10-13h-8Z" fill="currentColor" />;
    case 'water':
      return (
        <>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" style={{ stopColor: 'var(--tm-water-highlight)' }} />
              <stop offset="1" style={{ stopColor: 'currentColor' }} />
            </linearGradient>
          </defs>
          <path d="M12 1C10 6 4 11 4 16a8 8 0 0 0 16 0c0-5-6-10-8-15Z" fill={`url(#${id})`} />
          <path
            d="M7.6 14.4c0-2 1-3.2 2-5"
            fill="none"
            stroke="var(--tm-water-highlight)"
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.85"
          />
        </>
      );
    case 'fuel':
      return (
        <>
          <path
            fill="currentColor"
            fillRule="evenodd"
            d="M4 2h9a1 1 0 0 1 1 1v17h1v2H2v-2h1V3a1 1 0 0 1 1-1Zm1 3v6h7V5H5Z"
          />
          <path
            d="M14 12h2v6a2.5 2.5 0 0 0 5 0V8l-4-4m1 1-1 3 4 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'waste':
      return (
        <>
          <path d="M6 8.4h12l-1.2 11.6a2 2 0 0 1-2 1.8H9.2a2 2 0 0 1-2-1.8Z" fill="currentColor" />
          <rect x="4" y="5.4" width="16" height="2.4" rx="1.2" fill="currentColor" />
          <rect x="9.4" y="2.4" width="5.2" height="2.4" rx="1" fill="currentColor" />
        </>
      );
    case 'sun':
      return (
        <>
          <SunDisc id={id} cx={12} cy={12} r={5.5} />
          <path
            d="M12 1v2m0 18v2M1 12h2m18 0h2M4 4l2 2m12 12 2 2M4 20l2-2M18 6l2-2"
            fill="none"
            stroke="var(--tm-sun)"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </>
      );
    case 'partly-cloudy':
      return (
        <g transform="scale(0.75)">
          <SunDisc id={`${id}-s`} cx={21} cy={11} r={7} />
          <path
            d="M21 1v2m0 16v2M11 11h2m16 0h2M14 4l1.5 1.5M27 17l1.5 1.5M14 18l1.5-1.5M27 5l1.5-1.5"
            fill="none"
            stroke="var(--tm-sun)"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <CloudFill id={`${id}-c`} />
          <path
            d="M8 28a6 6 0 0 1-1-12 8 8 0 0 1 15-1 6.5 6.5 0 0 1 0 13Z"
            fill={`url(#${id}-c)`}
            stroke="var(--tm-cloud-edge)"
            strokeWidth=".8"
          />
        </g>
      );
    case 'cloud':
      return (
        <>
          <CloudFill id={id} />
          <path
            d="M6 19a5 5 0 0 1-1-10 7 7 0 0 1 13-1 5.5 5.5 0 0 1 0 11Z"
            fill={`url(#${id})`}
            stroke="var(--tm-cloud-edge)"
            strokeWidth=".8"
          />
        </>
      );
    case 'moon':
      return <path d="M20 15A9 9 0 0 1 9 4a9 9 0 1 0 11 11Z" fill="var(--tm-moon)" />;
    case 'wind':
      return (
        <path
          d="M3 8.5h11a3 3 0 1 0-3-3M3 12.5h16a3 3 0 1 1-3 3M3 16.5h8"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
        />
      );
    case 'thermometer':
      return (
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M9 4.5a3 3 0 0 1 6 0v8.6a5.5 5.5 0 1 1-6 0Zm3 15.3a2.4 2.4 0 0 0 1.2-4.5V7h-2.4v8.3A2.4 2.4 0 0 0 12 19.8Z"
        />
      );
    case 'barometer':
      return (
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M12 2a10 10 0 1 1 0 20 10 10 0 0 1 0-20Zm0 3.2a6.8 6.8 0 0 0-5.9 10.2h11.8A6.8 6.8 0 0 0 12 5.2Zm3.6 2.6-2.4 5.3a1.6 1.6 0 1 1-2-.9Z"
        />
      );
    case 'tide':
      return (
        <path
          fill="currentColor"
          d="M2 9.6c2.2 0 2.9-1.8 5-1.8s2.8 1.8 5 1.8 2.9-1.8 5-1.8 2.8 1.8 5 1.8v3c-2.2 0-2.9-1.8-5-1.8s-2.8 1.8-5 1.8-2.9-1.8-5-1.8-2.8 1.8-5 1.8Zm0 6.2c2.2 0 2.9-1.8 5-1.8s2.8 1.8 5 1.8 2.9-1.8 5-1.8 2.8 1.8 5 1.8v5.4H2Z"
        />
      );
    case 'depth':
      return (
        <>
          <path
            fill="currentColor"
            d="M2 6.6c2.2 0 2.9-1.8 5-1.8s2.8 1.8 5 1.8 2.9-1.8 5-1.8 2.8 1.8 5 1.8v2.8c-2.2 0-2.9-1.8-5-1.8s-2.8 1.8-5 1.8-2.9-1.8-5-1.8-2.8 1.8-5 1.8Z"
          />
          <path fill="currentColor" d="M10.6 10.5h2.8v6.3h3.1L12 22l-4.5-5.2h3.1Z" />
        </>
      );
    case 'engine':
      return (
        <path
          fill="currentColor"
          d="M7 4h7v2h-2v1.6h3.4l2 2.4H20a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-2.2l-2 2H8.5L6 17.6H4.6V19H2v-8h2.6v1.4H6V8.6A1 1 0 0 1 7 7.6h3V6H7Z"
        />
      );
    case 'helm':
      return (
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M12 1.5 14.4 9.6 22.5 12l-8.1 2.4L12 22.5l-2.4-8.1L1.5 12l8.1-2.4Zm0 8.7a1.8 1.8 0 1 0 0 3.6 1.8 1.8 0 0 0 0-3.6Z"
        />
      );
    case 'vessel':
      return (
        <path
          fill="currentColor"
          d="M11 2h2v3.2h3.6a1 1 0 0 1 1 1v5.2l2.9 1a1 1 0 0 1 .6 1.3L19 19.6a5.4 5.4 0 0 1-7 0 5.4 5.4 0 0 1-7 0l-2.1-5.9a1 1 0 0 1 .6-1.3l2.9-1V6.2a1 1 0 0 1 1-1H11ZM8.4 7.6v3l3.6-1.2 3.6 1.2v-3Z"
        />
      );
    case 'anchor':
      return (
        <>
          <circle cx="12" cy="4.4" r="2.2" fill="none" stroke="currentColor" strokeWidth="2.2" />
          <path
            d="M12 6.6V21M5 11.5H2.5v2a9.5 7.5 0 0 0 19 0v-2H19M8 10h8"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      );
    case 'pin':
      return (
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M20 10c0 6-8 13-8 13S4 16 4 10a8 8 0 0 1 16 0Zm-5.5 0a2.5 2.5 0 1 0-5 0 2.5 2.5 0 0 0 5 0Z"
        />
      );
    case 'spark':
      return (
        <path fill="currentColor" d="m12 2 2.5 7.5L22 12l-7.5 2.5L12 22l-2.5-7.5L2 12l7.5-2.5Z" />
      );
    case 'speaker':
      return (
        <>
          <path fill="currentColor" d="M3 9h4l5.5-4.5v15L7 15H3Z" />
          <path
            d="M16 8.5a5 5 0 0 1 0 7M18.8 5.8a9 9 0 0 1 0 12.4"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </>
      );
    case 'camera':
      return (
        <>
          <rect x="2" y="6" width="14" height="12" rx="2.5" fill="currentColor" />
          <path
            d="m17 10.6 4.2-2.8a.5.5 0 0 1 .8.4v7.6a.5.5 0 0 1-.8.4L17 13.4Z"
            fill="currentColor"
          />
        </>
      );
    case 'warning':
      return (
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M10.3 3.4a2 2 0 0 1 3.4 0l8.6 15.1A2 2 0 0 1 20.6 21H3.4a2 2 0 0 1-1.7-2.5Zm.5 5.6v5.4h2.4V9Zm1.2 7.3a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8Z"
        />
      );
  }
}

/** One filled symbol at `size` px, in the colour its CSS class gives it. */
export function InstrumentGlyph({
  name,
  size = 24,
  className,
}: {
  name: GlyphName;
  size?: number;
  className?: string;
}) {
  // Each drawn symbol owns its gradients: a board draws many of the same.
  const id = useId();
  return (
    <svg
      className={className === undefined ? 'tm-glyph' : `tm-glyph ${className}`}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      data-glyph={name}
    >
      {body(name, id)}
    </svg>
  );
}
